<script>
import Sortable from 'sortablejs'

export default {
    name: 'DraggableTransfer',
    props: {
        // [{ key, label, disabled }]
        transferData: {
            type: Array,
            required: true,
            validator(value) {
                if (!Array.isArray(value)) return false
                return value.every(
                    (el) => typeof el === 'object' && 'key' in el && 'label' in el && 'disabled' in el,
                )
            },
        },
        // 右パネルに移さない（＝左パネルに残す）キーの初期配列
        initialSelected: {
            type: Array,
            default: () => [],
            validator(value) {
                if (!Array.isArray(value)) return false
                return value.every((el) => typeof el === 'string' || typeof el === 'number')
            },
        },
        leftPanelTitle: {
            type: String,
            required: true,
        },
        rightPanelTitle: {
            type: String,
            required: true,
        },
        draggable: {
            type: Boolean,
            default: false,
        },
        // true の場合、disabled 項目は右パネル候補から除外する
        excludeDisabledFromRight: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            transferValue: [],                 // el-transfer の v-model（＝右パネルのキー配列）
            internalTransferData: this.transferData, // 並び順保持用の内部コピー
        }
    },

    watch: {
        transferData: {
            immediate: true,
            handler(val) {
                this.internalTransferData = val
                this.rebuildTransferValue()
            },
        },
        initialSelected: {
            immediate: true,
            deep: true,
            handler() {
                this.rebuildTransferValue()
            },
        },
    },

    mounted() {
        this.setupSortable()
    },

    methods: {
        // Transfer 行の描画（ドラッグハンドル付）
        renderContent(h, option) {
            if (!this.draggable) return h('span', option.label)
            return h(
                'div',
                { class: 'transfer-item', attrs: { 'data-key': option.key } },
                [h('span', { class: 'drag-handle' }, ' ☰ '), h('span', { class: 'transfer-label' }, option.label)],
            )
        },

        rebuildTransferValue() {
            // 右パネル（transferValue）＝全キー − initialSelected − disabled
            const allKeys = (this.internalTransferData || []).map((el) => el.key)
            const init = Array.isArray(this.initialSelected) ? this.initialSelected : []
            const disabledKeySet = this.getDisabledKeySet()

            let next = allKeys
                .filter((k) => !init.some((x) => x === k))

            if (this.excludeDisabledFromRight) {
                next = next.filter((k) => !disabledKeySet.has(String(k)))
            }

            this.transferValue = next
        },

        // 選択変更（左右移動）時に左パネル側のキー配列を emit
        onChangeSelected() {
            this.normalizeTransferValue()

            const selectedLeft = (this.internalTransferData || [])
                .map((el) => el.key)
                .filter((k) => !this.transferValue.some((r) => r === k))
            this.$emit('select', selectedLeft)
        },

        setupSortable() {
            if (!this.draggable) return

            const root =
                this.$refs.transferRef && this.$refs.transferRef.$el
                    ? this.$refs.transferRef.$el
                    : this.$refs.transferRef
            if (!root) return

            const panels = root.querySelectorAll('.el-transfer-panel')
            if (!panels || panels.length < 2) return

            const leftList = panels[0].querySelector('.el-transfer-panel__list')
            const rightList = panels[1].querySelector('.el-transfer-panel__list')

            if (leftList) {
                Sortable.create(leftList, {
                    animation: 150,
                    handle: '.drag-handle',
                    onEnd: () => {
                        const items = Array.from(leftList.querySelectorAll('.transfer-item'))
                        const newOrder = items
                            .map((el) => {
                                const key = el.getAttribute('data-key')
                                return (this.internalTransferData || []).find(
                                    (it) => String(it.key) === String(key),
                                )
                            })
                            .filter(Boolean)

                        // 左パネル順を内部コピーに反映（右側にある要素は末尾維持）
                        this.internalTransferData = [
                            ...newOrder,
                            ...(this.internalTransferData || []).filter(
                                (it) => !newOrder.some((n) => n.key === it.key),
                            ),
                        ]

                        this.onChangeSelected()
                    },
                })
            }

            if (rightList) {
                const hideHandles = () => {
                    rightList.querySelectorAll('.drag-handle').forEach((el) => (el.style.display = 'none'))
                }
                hideHandles()
                const observer = new MutationObserver(hideHandles)
                observer.observe(rightList, { childList: true, subtree: true })
            }
        },

        getDisabledKeySet() {
            return new Set(
                (this.internalTransferData || [])
                    .filter((it) => it && it.disabled)
                    .map((it) => String(it.key)),
            )
        },

        // transferValue から disabled を除外して正規化
        normalizeTransferValue() {
            const disabledKeySet = this.getDisabledKeySet()
            if (!disabledKeySet.size) return

            const next = (this.transferValue || []).filter((k) => !disabledKeySet.has(String(k)))

            if (next.length !== (this.transferValue || []).length) {
                this.transferValue = next
            }
        },
    },
}
</script>

<template>
    <div class="transfer-row">
        <el-transfer
            ref="transferRef"
            v-model="transferValue"
            :data="internalTransferData"
            :button-texts="['追加', '削除']"
            :titles="[leftPanelTitle, rightPanelTitle]"
            :render-content="renderContent"
            @change="onChangeSelected"
        />
    </div>
</template>

<style scoped>
/* --- 横並びレイアウトを強制（折り返し禁止） --- */
.transfer-row :deep(.el-transfer) {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;      /* ← これが無いと縦に折り返される */
    align-items: stretch;
    gap: 16px;
    width: 100%;
}

/* パネルは等分の可変幅に。はみ出しで折り返さないため min-width:0 を付与 */
.transfer-row :deep(.el-transfer-panel) {
    flex: 1 1 0;
    min-width: 0;                       /* ← Flex 子要素のはみ出し対策 */
    width: auto !important;             /* 既定の固定幅を打ち消す */
}

/* 中央ボタンは固定幅・縦積み */
.transfer-row :deep(.el-transfer__buttons) {
    flex: 0 0 auto;
    align-self: center;
    display: inline-flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
}

/* 2つめのボタンのマージン消去 */
.transfer-row :deep(.el-transfer__buttons .el-button) {
    margin: 0;
}

/* 非活性（disabled）時の見た目（disable-command-button に合わせる） */
.transfer-row :deep(.el-transfer__buttons .el-button.is-disabled),
.transfer-row :deep(.el-transfer__buttons .el-button.is-disabled:hover),
.transfer-row :deep(.el-transfer__buttons .el-button.is-disabled:active),
.transfer-row :deep(.el-transfer__buttons .el-button.is-disabled:focus) {
    background-color: #5171cb !important;
    color: #ffffff !important;
    opacity: 0.3 !important;
    cursor: not-allowed !important;
}

/* デフォルトの矢印アイコン(<、>)非表示 */
.transfer-row :deep(.el-icon-arrow-left),
.transfer-row :deep(.el-icon-arrow-right) {
    display: none;
}

/* 左(上)側ボタンアイコン */
.transfer-row :deep(.el-transfer__button:nth-child(1)::before) {
    display: inline-block;
    width: 14px;
    height: 14px;
    content: url('@/assets/images/back.svg');
}

/* 右(下)側ボタンアイコン */
.transfer-row :deep(.el-transfer__button:nth-child(2)::after) {
    display: inline-block;
    width: 14px;
    height: 14px;
    content: url('@/assets/images/right.svg');
}

/* パネルヘッダーの見た目調整（Element UI 向け） */
.transfer-row :deep(.el-transfer-panel__header) {
    padding: 0;
    background: none;
    border: none;
}
.transfer-row :deep(.el-transfer-panel__body) {
    border-top: 1px solid #ebeef5;
}
.transfer-row :deep(.el-transfer-panel__header .el-checkbox) {
    background-color: #f5f5f5;
}

/* パネルヘッダー選択中アイテム数非表示 */
.transfer-row :deep(.el-transfer-panel__header .el-checkbox__label span) {
    display: none;
}

/* ドラッグ用スタイル */
.transfer-item {}
.drag-handle {
    margin-right: 8px;
    cursor: grab;
    user-select: none;
}
</style>
